import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { getCoupons, createCoupon, updateCoupon, deleteCoupon, type Coupon, type CouponType } from '@/services'
import { AppButton, EmptyState, FilterChip, StatusBadge, Modal, IconButton, Skeleton, TableSkeleton } from '@/components/platform/primitives'
import { formatDate } from '@/lib/utils'

function couponValueLabel(c: Coupon) {
  return c.type === 'percent' ? `${c.value}%` : `$${c.value.toLocaleString('es-AR')} ${c.currency || ''}`.trim()
}

export function CouponsPage() {
  const qc = useQueryClient()
  const [filter, setFilter] = useState('all')
  const [editing, setEditing] = useState<Coupon | null>(null)
  const [creating, setCreating] = useState(false)

  const { data: result, isLoading } = useQuery({
    queryKey: ['coupons', filter],
    queryFn: () => getCoupons({ isActive: filter === 'all' ? undefined : filter === 'active', pageSize: 200 }),
  })

  const coupons = result?.data || []
  const total = result?.count || coupons.length
  const activeCount = coupons.filter((c) => c.is_active).length

  const invalidate = () => qc.invalidateQueries({ queryKey: ['coupons'] })

  const saveMutation = useMutation({
    mutationFn: async (form: { id?: string; code: string; type: CouponType; value: number; description?: string | null; max_redemptions?: number | null; expires_at?: string | null; is_active: boolean }) => {
      if (form.id) {
        return updateCoupon(form.id, { type: form.type, value: form.value, description: form.description, max_redemptions: form.max_redemptions, expires_at: form.expires_at, is_active: form.is_active })
      }
      return createCoupon({ code: form.code, type: form.type, value: form.value, description: form.description, max_redemptions: form.max_redemptions, expires_at: form.expires_at, is_active: form.is_active })
    },
    onSuccess: () => { toast.success('Cupón guardado'); invalidate(); setEditing(null); setCreating(false) },
    onError: (err: any) => toast.error(err?.message || 'No se pudo guardar el cupón'),
  })

  const toggleMutation = useMutation({
    mutationFn: (c: Coupon) => updateCoupon(c.id, { is_active: !c.is_active }),
    onSuccess: () => { toast.success('Cupón actualizado'); invalidate() },
    onError: (err: any) => toast.error(err?.message || 'No se pudo actualizar'),
  })

  const deleteMutation = useMutation({
    mutationFn: (c: Coupon) => deleteCoupon(c.id),
    onSuccess: () => { toast.success('Cupón eliminado'); invalidate() },
    onError: (err: any) => toast.error(err?.message || 'No se pudo eliminar'),
  })

  const modalOpen = creating || !!editing

  return (
    <>
      <div className="page-header">
        <div className="page-title-block">
          <div className="eyebrow">Billing</div>
          <h1>Cupones</h1>
          <div className="subtitle">{isLoading ? <Skeleton width={190} height={14} /> : `${total} cupones · ${activeCount} activos`}</div>
        </div>
        <div className="page-actions">
          <AppButton variant="primary" icon="plus" onClick={() => { setEditing(null); setCreating(true) }}>Nuevo cupón</AppButton>
        </div>
      </div>

      <div className="table-wrap">
        <div className="table-toolbar">
          <FilterChip active={filter === 'all'} onClick={() => setFilter('all')} label="Todos" count={isLoading ? undefined : total} />
          <FilterChip active={filter === 'active'} onClick={() => setFilter('active')} label="Activos" count={isLoading ? undefined : activeCount} />
          <FilterChip active={filter === 'inactive'} onClick={() => setFilter('inactive')} label="Inactivos" count={isLoading ? undefined : total - activeCount} />
        </div>

        {isLoading ? (
          <TableSkeleton columns={7} rows={8} />
        ) : coupons.length === 0 ? (
          <EmptyState icon="tag" title="Sin cupones" desc="Creá el primer cupón de descuento." />
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th>Código</th>
                <th>Tipo</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
                <th style={{ textAlign: 'right' }}>Usos</th>
                <th>Vence</th>
                <th>Status</th>
                <th style={{ width: 90 }} />
              </tr>
            </thead>
            <tbody>
              {coupons.map((c) => (
                <tr key={c.id}>
                  <td>
                    <div className="cell-primary"><span className="code-chip" style={{ fontWeight: 600 }}>{c.code}</span></div>
                    {c.description ? <div className="cell-secondary">{c.description}</div> : null}
                  </td>
                  <td><span className="badge badge-outline badge-sm">{c.type === 'percent' ? 'Porcentaje' : 'Fijo'}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular cell-primary">{couponValueLabel(c)}</span></td>
                  <td style={{ textAlign: 'right' }}><span className="tabular">{c.redemptions || 0}{c.max_redemptions ? ` / ${c.max_redemptions}` : ''}</span></td>
                  <td><span style={{ fontSize: 12, color: 'var(--text-3)' }}>{c.expires_at ? formatDate(c.expires_at) : 'Sin vencimiento'}</span></td>
                  <td><StatusBadge status={c.is_active ? 'active' : 'cancelled'} size="sm" /></td>
                  <td>
                    <div className="row gap-2">
                      <IconButton icon="edit" tooltip="Editar" size={13} onClick={() => { setCreating(false); setEditing(c) }} />
                      <IconButton icon={c.is_active ? 'pause' : 'play'} tooltip={c.is_active ? 'Desactivar' : 'Activar'} size={13} onClick={() => toggleMutation.mutate(c)} />
                      <IconButton icon="trash2" tooltip="Eliminar" size={13} onClick={() => { if (window.confirm(`¿Eliminar el cupón ${c.code}?`)) deleteMutation.mutate(c) }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={() => { setEditing(null); setCreating(false) }}
        title={editing ? `Editar ${editing.code}` : 'Nuevo cupón'}
        footer={
          <div className="row gap-2" style={{ justifyContent: 'flex-end' }}>
            <AppButton variant="ghost" onClick={() => { setEditing(null); setCreating(false) }}>Cancelar</AppButton>
            <AppButton variant="primary" icon="check" disabled={saveMutation.isPending} onClick={() => {
              const form = document.getElementById('coupon-form') as HTMLFormElement | null
              if (form) form.requestSubmit()
            }}>{saveMutation.isPending ? 'Guardando…' : 'Guardar'}</AppButton>
          </div>
        }
      >
        {modalOpen ? (
          <form
            id="coupon-form"
            onSubmit={(e) => {
              e.preventDefault()
              const fd = new FormData(e.currentTarget)
              saveMutation.mutate({
                id: editing?.id,
                code: String(fd.get('code') || '').trim(),
                type: (fd.get('type') as CouponType) || 'percent',
                value: Number(fd.get('value') || 0),
                description: String(fd.get('description') || '') || null,
                max_redemptions: fd.get('max_redemptions') ? Number(fd.get('max_redemptions')) : null,
                expires_at: fd.get('expires_at') ? new Date(String(fd.get('expires_at'))).toISOString() : null,
                is_active: fd.get('is_active') === 'on',
              })
            }}
            className="col gap-4"
          >
            <div className="form-row">
              <label htmlFor="cp-code">Código</label>
              <input id="cp-code" className="input" name="code" defaultValue={editing?.code || ''} placeholder="ej: BIENVENIDA20" required disabled={!!editing} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-row">
                <label htmlFor="cp-type">Tipo</label>
                <select id="cp-type" className="select" name="type" defaultValue={editing?.type || 'percent'}>
                  <option value="percent">Porcentaje (%)</option>
                  <option value="fixed">Monto fijo</option>
                </select>
              </div>
              <div className="form-row">
                <label htmlFor="cp-value">Valor</label>
                <input id="cp-value" className="input" name="value" type="number" min={0} step="0.01" defaultValue={editing?.value ?? ''} required />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div className="form-row">
                <label htmlFor="cp-max">Máx. usos (opcional)</label>
                <input id="cp-max" className="input" name="max_redemptions" type="number" min={0} defaultValue={editing?.max_redemptions ?? ''} />
              </div>
              <div className="form-row">
                <label htmlFor="cp-expires">Vencimiento (opcional)</label>
                <input id="cp-expires" className="input" name="expires_at" type="date" defaultValue={editing?.expires_at ? editing.expires_at.slice(0, 10) : ''} />
              </div>
            </div>
            <div className="form-row">
              <label htmlFor="cp-desc">Descripción</label>
              <input id="cp-desc" className="input" name="description" defaultValue={editing?.description || ''} placeholder="Para uso interno" />
            </div>
            <label className="toggle">
              <input type="checkbox" name="is_active" defaultChecked={editing ? editing.is_active : true} />
              Activo
            </label>
          </form>
        ) : null}
      </Modal>
    </>
  )
}
